import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAgreementById } from '../services/agreementService';
import { useAgreement } from '../context/AgreementContext';
import { AgreementNotFound } from './AgreementNotFound';
import { AgreementExpired } from './AgreementExpired';
import { AgreementDeleted } from './AgreementDeleted';

interface StatusGuardProps {
  children: React.ReactNode;
  allowedStatuses?: string[];
  redirectOnCompleted?: boolean;
}

export function StatusGuard({ 
  children, 
  allowedStatuses = [], 
  redirectOnCompleted = true 
}: StatusGuardProps) {
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useAgreement();
  const [isLoading, setIsLoading] = useState(true);
  const [agreement, setAgreement] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    const checkAgreementStatus = async () => {
      if (!agreement_id) {
        setIsLoading(false);
        return;
      }

      try {
        console.log('StatusGuard: Checking agreement status for ID:', agreement_id);
        const agreementData = await getAgreementById(agreement_id);
        
        if (agreementData) {
          setAgreement(agreementData);
          
          // Load agreement data into context
          dispatch({ type: 'LOAD_AGREEMENT', payload: agreementData });
          
          console.log('StatusGuard: Agreement status:', agreementData.status);
          console.log('StatusGuard: Current path:', location.pathname);
          
          // PRIORITY CHECK 1: Check if agreement is deleted FIRST
          // This overrides any other redirections or status checks
          if (agreementData.deleted) {
            console.log('StatusGuard: Agreement is deleted (deleted:', agreementData.deleted, '), showing deleted page (overriding any other redirections)');
            setDeleted(true);
            return;
          }
          
          // PRIORITY CHECK 2: Check if agreement is expired SECOND
          // This overrides any other redirections or status checks (except deleted)
          // Check if agreement has an expiry_date and if it's past the current date
          if (agreementData.expiry_date) {
            const expiredDate = new Date(agreementData.expiry_date);
            const currentDate = new Date();
            
            if (currentDate > expiredDate) {
              console.log('StatusGuard: Agreement is expired (expiry_date:', agreementData.expiry_date, '), showing expired page (overriding any other redirections)');
              setExpired(true);
              return;
            } else {
              console.log('StatusGuard: Agreement has expiry_date but is not yet expired:', agreementData.expiry_date);
            }
          }
          
          // Handle specific status-based redirections only for completed agreements
          // Other access control is handled by route-level allowedStatuses
          if (agreementData.status === 'completed') {
            const isConfirmationPage = location.pathname.includes('/confirmation');
            if (!isConfirmationPage) {
              console.log('StatusGuard: Completed agreement, redirecting to confirmation page');
              navigate(`/${agreement_id}/confirmation`, { replace: true });
              return;
            }
          }
          
          // If we have allowed statuses and the current status is not allowed
          if (allowedStatuses.length > 0 && !allowedStatuses.includes(agreementData.status)) {
            console.log('StatusGuard: Status not allowed for this page, redirecting based on status');
            
            // Redirect based on status using the same rules as above
            // Note: 'expired' is handled above and will never reach this point
            switch (agreementData.status) {
              case 'completed':
                navigate(`/${agreement_id}/confirmation`, { replace: true });
                break;
              case 'confirmed':
              case 'signed':
              case 'pending':
              case 'reviewed':
              default:
                navigate(`/${agreement_id}`, { replace: true });
                break;
            }
            return;
          }
        }
      } catch (error) {
        console.error('StatusGuard: Error checking agreement status:', error);
        
        // Check if this is a 404 or 422 error (agreement not found)
        if (error && typeof error === 'object' && ((error as any).status === 404 || (error as any).status === 422)) {
          console.log(`StatusGuard: Agreement not found (${(error as any).status}), showing not found page`);
          setNotFound(true);
        } else {
          console.error('StatusGuard: Other error occurred:', error);
          // For other errors, we could show a generic error page or handle differently
          // For now, we'll just log the error and continue
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAgreementStatus();
  }, [agreement_id, navigate, location.pathname, allowedStatuses, redirectOnCompleted, dispatch]);

  // Show agreement not found page if 404 error occurred
  if (notFound) {
    return <AgreementNotFound />;
  }

  // Show agreement deleted page if agreement is deleted
  if (deleted) {
    return <AgreementDeleted />;
  }

  // Show agreement expired page if agreement is expired
  if (expired) {
    return <AgreementExpired />;
  }

  // Show loading state while checking status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If status checks pass, render children
  return <>{children}</>;
}